import React, { createContext, useEffect, useMemo, useState } from 'react';
import { ASSET_ACQUISITION_MODAL_ID } from '../constants/modalConstants';
import statusTypes from '../support/statusTypes';
import useStatus from '../hooks/stateful/useStatus';
import acquisitionMode from '../support/acquisitionMode';

interface AssetAcquisitionModalContextValue {
  modal: string;
  setModal: (modal: string) => void;
  closeModal: () => void;
}

interface AssetAcquisitionModalProviderProps {
  children: React.ReactNode;
  isAcquisitionModalOpen?: boolean;
  onCloseModal?: (() => void) | null;
  mode?: string;
}

const AssetAcquisitionModalContext = createContext<
  AssetAcquisitionModalContextValue | undefined
>(undefined);

const AssetAcquisitionModalProvider = ({
  children,
  isAcquisitionModalOpen = false,
  onCloseModal = null,
  mode = '',
}: AssetAcquisitionModalProviderProps) => {
  const [modal, setModal] = useState('');
  const { status } = useStatus();

  const closeModal = () => {
    setModal('');
    if (onCloseModal) {
      onCloseModal();
    }
  };

  useEffect(() => {
    setModal(
      mode === acquisitionMode.MODAL && isAcquisitionModalOpen
        ? ASSET_ACQUISITION_MODAL_ID
        : ''
    );
  }, [isAcquisitionModalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (status === statusTypes.COMPLETE) {
      closeModal();
    }
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  // closeModal is intentionally omitted: it's redefined every render (not
  // wrapped in useCallback), so listing it here would force this memo to
  // recompute on every render — wrapping closeModal in useCallback would fix
  // that, but changes its identity/behavior from master.
  const contextValue = useMemo(
    () => ({
      modal,
      setModal,
      closeModal,
    }),
    [modal] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <AssetAcquisitionModalContext.Provider value={contextValue}>
      {children}
    </AssetAcquisitionModalContext.Provider>
  );
};

export {
  AssetAcquisitionModalContext,
  AssetAcquisitionModalProvider,
  AssetAcquisitionModalContextValue,
};
